import api from './client'
import { Assessment, AssessmentSubmission, CreateAssessmentData, ApiResponse } from '../types'

export const getAssessments = async (): Promise<ApiResponse<Assessment[]>> => {
  try {
    // Fetch only standalone assessments
    const response = await api.get('/assessments/standalone/')
    
    // Handle paginated response - DRF PageNumberPagination returns { count, next, previous, results }
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      return {
        data: response.data.results || [],
        success: true
      }
    }
    
    // If response is already an array (shouldn't happen with DRF)
    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        success: true
      }
    }
    
    return {
      data: [],
      success: false
    }
  } catch (error: any) {
    console.error('🔴 Error fetching assessments:', error)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)
    throw error
  }
}

export const getAssessmentById = async (assessmentId: string): Promise<ApiResponse<Assessment>> => {
  const response = await api.get(`/assessments/${assessmentId}/`)
  
  // Some APIs might return direct data, others might wrap it
  if (response.data && typeof response.data === 'object') {
    // If it's a direct assessment object, return it
    if (response.data.id && response.data.title) {
      return {
        data: response.data,
        success: true
      }
    }
  }
  
  return response.data
}

export const createAssessment = async (assessmentData: CreateAssessmentData): Promise<ApiResponse<Assessment>> => {
  const response = await api.post('/assessments/', assessmentData)
  return response.data
}

export const updateAssessment = async (
  assessmentId: string, 
  assessmentData: Partial<CreateAssessmentData>
): Promise<ApiResponse<Assessment>> => {
  const response = await api.patch(`/assessments/${assessmentId}/`, assessmentData)
  return response.data
}

export const deleteAssessment = async (assessmentId: string): Promise<ApiResponse<null>> => {
  const response = await api.delete(`/assessments/${assessmentId}/`)
  return response.data
}

export const startAssessment = async (assessmentId: string): Promise<ApiResponse<AssessmentSubmission>> => {
  // For assessments, we don't need a separate start endpoint
  // The submission is created when the user first accesses the assessment
  // We'll create an in_progress submission here
  const response = await api.post(`/assessments/${assessmentId}/submit/`, {
    assessment: assessmentId,
    answers: [] // Empty answers to create in_progress submission
  })
  return response.data
}

export const submitAssessment = async (
  assessmentId: string, 
  answers: any[]
): Promise<AssessmentSubmission> => {
  const response = await api.post(`/assessments/${assessmentId}/submit/`, { 
    assessment: assessmentId,
    answers 
  })
  // The backend returns the submission object directly
  return response.data
}

export const getUserSubmissions = async (): Promise<ApiResponse<AssessmentSubmission[]>> => {
  const response = await api.get('/submissions/')
  return response.data
}

export const getLastAttemptResults = async (assessmentId: number) => {
  const response = await api.get(`/assessments/${assessmentId}/last_attempt_results/`)
  return response.data
}

export const assessmentService = {
  getAssessments,
  getAssessmentById,
  createAssessment,
  updateAssessment,
  deleteAssessment,
  startAssessment,
  submitAssessment,
  getUserSubmissions,
  getLastAttemptResults,
}
